import { LogicalOperators } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import { Card } from "../Card";
import {
  getStringKeyFromArrayKey,
  translateAndOr,
} from "../FilterBuilder/FilterBuilder.helpers";
import styles from "../FilterBuilder/FilterBuilder.module.scss";
import { FilterDisplayFieldValue } from "./Subcomponents/FilterDisplayFieldValue";
import { FilterDisplayProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A read-only component that displays filter objects in a human-readable format.
 * Similar to FilterBuilder but without any editing capabilities.
 */
export const FilterDisplay = ({
  fields,
  filter,
  title,
  style,
  variant = "default",
  className,
  _recursion = {
    currentJoinOperator: "",
    depth: 0,
    prefixKey: [],
  },
}: FilterDisplayProps) => {
  const isRootLevelOfRecursion = _recursion.depth === 0;

  const conditions = useMemo(() => {
    let calculatedConditions: Array<[string, unknown]> = [];

    if (Array.isArray(filter)) {
      calculatedConditions = filter.map((c) => {
        const k = Object.keys(c);
        if (k.length > 1)
          throw new Error(`No more than 1 condition in $or/$and: ${k.length}`);
        return [k[0], c[k[0]]];
      });
    } else {
      calculatedConditions = Object.entries(filter);
    }

    return calculatedConditions;
  }, [filter]);

  return (
    <Card
      className={cx("container", {
        [className ?? ""]: !!className && isRootLevelOfRecursion,
      })}
      colorVariant={_recursion.depth > 0 ? "grey" : "stone"}
      style={{
        ...(isRootLevelOfRecursion ? style : undefined),
        marginBottom: 0,
      }}
    >
      {isRootLevelOfRecursion && title && (
        <div className={cx("title")}>{title}</div>
      )}

      {conditions.map(
        ([fieldKey, fieldValue]: [string, unknown], conditionIndex) => {
          // Recursively built key:
          const myKey = [
            ..._recursion.prefixKey,
            ...(Array.isArray(filter) ? [conditionIndex] : []),
            fieldKey,
          ];

          return (
            <div
              key={getStringKeyFromArrayKey(myKey)}
              className={cx("condition-line", {
                "is-single-line":
                  !(
                    fieldKey === LogicalOperators.and ||
                    fieldKey === LogicalOperators.or
                  ) && variant !== "vertical",
              })}
              style={{ margin: "4px 0" }}
            >
              <div
                className={cx("label", "join-label", {
                  "active-label": conditions.length > 1,
                })}
              >
                {conditionIndex > 0 &&
                  translateAndOr(
                    _recursion.currentJoinOperator
                      ? _recursion.currentJoinOperator
                      : LogicalOperators.and
                  )}
              </div>
              {fieldKey === LogicalOperators.and ||
              fieldKey === LogicalOperators.or ? (
                <FilterDisplay
                  _recursion={{
                    prefixKey: myKey,
                    currentJoinOperator: fieldKey,
                    depth: _recursion.depth + 1,
                  }}
                  fields={fields}
                  filter={fieldValue}
                  variant={variant}
                />
              ) : (
                <FilterDisplayFieldValue
                  fieldDefinition={fields[fieldKey]}
                  fieldKey={fieldKey}
                  fieldValue={fieldValue}
                />
              )}
            </div>
          );
        }
      )}
    </Card>
  );
};
